import React from 'react';
import { Alert } from 'react-bootstrap';
import { FiAlertTriangle } from 'react-icons/fi';
import { useTranslation } from '../../hooks/useTranslation';
import { formatDateDdMmYyyy } from '../../utils/bankDetailsRemindersError';
import './BankDetailsRemindersErrorAlert.css';

const BankDetailsRemindersErrorAlert = ({ error, onClose }) => {
    const { t } = useTranslation();
    const prefix = 'pages.adminTripsDaily.reminders.errors';

    if (!error) return null;

    if (error.kind === 'invalid_trip_end_period') {
        const selectedEnd = formatDateDdMmYyyy(error.selectedEndDate);
        const todayParis = formatDateDdMmYyyy(error.todayParis);
        const maxAllowed = formatDateDdMmYyyy(error.maxAllowedDate);

        return (
            <Alert variant="danger" dismissible={!!onClose} onClose={onClose} className="reminders-error-alert">
                <div className="reminders-error-toast mb-3">
                    {t(`${prefix}.toastCompact`, { date: maxAllowed })}
                </div>
                <div className="d-flex align-items-start gap-2">
                    <FiAlertTriangle size={22} className="mt-1 flex-shrink-0" aria-hidden />
                    <div>
                        <Alert.Heading className="h5 mb-2">{t(`${prefix}.invalidTripEndTitle`)}</Alert.Heading>
                        <p className="mb-2">{t(`${prefix}.invalidTripEndIntro`)}</p>
                        <p className="mb-2">
                            {t(`${prefix}.invalidTripEndTooRecent`, { date: selectedEnd })}
                        </p>
                        <p className="mb-3">{t(`${prefix}.invalidTripEndRule`)}</p>

                        <div className="reminders-error-values mb-3">
                            <p className="mb-1"><strong>{t(`${prefix}.detectedValues`)}</strong></p>
                            <ul className="mb-0 ps-3">
                                <li>{t(`${prefix}.selectedEndDate`)} : <strong>{selectedEnd}</strong></li>
                                <li>{t(`${prefix}.todayDate`)} : <strong>{todayParis}</strong></li>
                                <li>{t(`${prefix}.maxAllowedDate`)} : <strong>{maxAllowed}</strong></li>
                            </ul>
                        </div>

                        <p className="mb-1"><strong>{t(`${prefix}.actionTitle`)}</strong></p>
                        <p className="mb-0">
                            {t(`${prefix}.invalidTripEndAction`, { date: maxAllowed })}
                        </p>
                    </div>
                </div>
            </Alert>
        );
    }

    if (error.kind === 'forbidden_company') {
        const allowed = Array.isArray(error.allowedCompanyNames) ? error.allowedCompanyNames : [];
        return (
            <Alert variant="danger" dismissible={!!onClose} onClose={onClose} className="reminders-error-alert">
                <div className="d-flex align-items-start gap-2">
                    <FiAlertTriangle size={22} className="mt-1 flex-shrink-0" aria-hidden />
                    <div>
                        <Alert.Heading className="h5 mb-2">{t(`${prefix}.forbiddenCompanyTitle`)}</Alert.Heading>
                        <p className="mb-2">
                            {error.message || t(`${prefix}.forbiddenCompanyMessage`, {
                                company: error.companyName || '—',
                            })}
                        </p>
                        {error.companyName && (
                            <p className="mb-2">
                                {t(`${prefix}.selectedCompany`)} : <strong>{error.companyName}</strong>
                            </p>
                        )}
                        {allowed.length > 0 && (
                            <div className="mb-2">
                                <p className="mb-1"><strong>{t(`${prefix}.allowedCompanies`)}</strong></p>
                                <ul className="mb-0 ps-3">
                                    {allowed.map((name) => (
                                        <li key={name}><code>{name}</code></li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <p className="mb-0">{t(`${prefix}.forbiddenCompanyAction`)}</p>
                    </div>
                </div>
            </Alert>
        );
    }

    if (error.kind === 'invalid_period_order') {
        return (
            <Alert variant="danger" dismissible={!!onClose} onClose={onClose} className="reminders-error-alert">
                <div className="d-flex align-items-start gap-2">
                    <FiAlertTriangle size={22} className="mt-1 flex-shrink-0" aria-hidden />
                    <div>
                        <Alert.Heading className="h5 mb-2">{t(`${prefix}.invalidPeriodTitle`)}</Alert.Heading>
                        <p className="mb-2">{t(`${prefix}.invalidPeriodMessage`)}</p>
                        {(error.selectedStartDate || error.selectedEndDate) && (
                            <ul className="mb-2 ps-3">
                                {error.selectedStartDate && (
                                    <li>{t('pages.adminTripsDaily.reminders.dateStart')} : <strong>{formatDateDdMmYyyy(error.selectedStartDate)}</strong></li>
                                )}
                                {error.selectedEndDate && (
                                    <li>{t('pages.adminTripsDaily.reminders.dateEnd')} : <strong>{formatDateDdMmYyyy(error.selectedEndDate)}</strong></li>
                                )}
                            </ul>
                        )}
                        <p className="mb-0">{t(`${prefix}.invalidPeriodAction`)}</p>
                    </div>
                </div>
            </Alert>
        );
    }

    return (
        <Alert variant="danger" dismissible={!!onClose} onClose={onClose} className="reminders-error-alert">
            <div className="d-flex align-items-start gap-2">
                <FiAlertTriangle size={22} className="mt-1 flex-shrink-0" aria-hidden />
                <div>
                    <Alert.Heading className="h5 mb-2">{t(`${prefix}.genericTitle`)}</Alert.Heading>
                    <p className="mb-0">{error.message || t('pages.adminTripsDaily.reminders.emailError')}</p>
                </div>
            </div>
        </Alert>
    );
};

export default BankDetailsRemindersErrorAlert;
